import { useState, useMemo, useEffect, useCallback, useRef } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { Plus, Search, FileText, Mail, MoreVertical, Pencil, Copy, Trash2, Star, Loader2 } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from '@/components/ui/alert-dialog';
import { type Template } from '@/types/template';
import { useTemplatesList, useDeleteTemplate } from '@/hooks/useTemplatesData';
import { useMode } from '@/providers/ModeProvider';
import { ApiError } from '@/providers/ApiConfigProvider';
import { toast } from 'sonner';

interface DocumentTemplatesPageProps {
  /** When provided these callbacks are used instead of `navigate` so the page
   *  can be embedded inside a parent (e.g. the onboarding wizard) without
   *  triggering a full route change. */
  onCreateTemplate?: () => void;
  onEditTemplate?: (template: Template) => void;
  onDuplicateTemplate?: (template: Template) => void;
}

export function DocumentTemplatesPage({
  onCreateTemplate,
  onEditTemplate,
  onDuplicateTemplate,
}: DocumentTemplatesPageProps = {}) {
  const navigate = useNavigate();
  const location = useLocation();
  const [activeTab, setActiveTab] = useState<'all' | 'email' | 'statement'>('all');
  const [searchTerm, setSearchTerm] = useState('');
  const [sortOption, setSortOption] = useState<'name-asc' | 'name-desc' | 'newest' | 'oldest'>('name-asc');
  
  // Dialog states
  const [isDeleteDialogOpen, setIsDeleteDialogOpen] = useState(false);
  const [isDefaultDialogOpen, setIsDefaultDialogOpen] = useState(false);
  const [selectedTemplate, setSelectedTemplate] = useState<Template | null>(null);
  const [localTemplateOverrides, setLocalTemplateOverrides] = useState<Template[] | null>(null);

  const { data: apiTemplates, isLoading } = useTemplatesList();
  const deleteTemplateMutation = useDeleteTemplate();
  const { isDemo } = useMode();

  const templates = useMemo(() => localTemplateOverrides ?? apiTemplates ?? [], [localTemplateOverrides, apiTemplates]);

  const handledSavedTemplateRef = useRef(false);

  // Handle saved template from editor page, and clear local overrides when API data refreshes
  useEffect(() => {
    if (location.state?.savedTemplate && !handledSavedTemplateRef.current) {
      handledSavedTemplateRef.current = true;
      const { savedTemplate, mode } = location.state;
      setLocalTemplateOverrides(prev => {
        const base = prev ?? apiTemplates ?? [];
        if (mode === 'create') {
          return [...base, savedTemplate];
        }
        return base.map(x => x.id === savedTemplate.id ? savedTemplate : x);
      });
      window.history.replaceState({}, document.title);
    } else if (apiTemplates) {
      setLocalTemplateOverrides(null);
    }
  }, [location.state, apiTemplates]);

  // Filter and sort templates (handles demo mode client-side; live mode data is pre-filtered by API)
  const filteredAndSortedTemplates = useMemo(() => {
    let filtered = templates;

    if (activeTab !== 'all') {
      filtered = filtered.filter(template => template.type === activeTab);
    }

    if (searchTerm) {
      filtered = filtered.filter(template =>
        template.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
        template.category?.some(cat => cat.toLowerCase().includes(searchTerm.toLowerCase()))
      );
    }

    const sorted = [...filtered].sort((a, b) => {
      switch (sortOption) {
        case 'name-asc':
          return a.name.localeCompare(b.name);
        case 'name-desc':
          return b.name.localeCompare(a.name);
        case 'newest':
          return new Date(b.lastModified).getTime() - new Date(a.lastModified).getTime();
        case 'oldest':
          return new Date(a.lastModified).getTime() - new Date(b.lastModified).getTime();
        default:
          return 0;
      }
    });

    return sorted;
  }, [templates, activeTab, searchTerm, sortOption]);

  const templateCounts = useMemo(() => {
    return {
      all: templates.length,
      email: templates.filter(t => t.type === 'email').length,
      statement: templates.filter(t => t.type === 'statement').length,
    };
  }, [templates]);

  const handleCreateTemplate = () => {
    if (onCreateTemplate) {
      onCreateTemplate();
    } else {
      navigate('/settings/templates/create', { state: { mode: 'create' } });
    }
  };

  const handleEditTemplate = (template: Template) => {
    if (onEditTemplate) {
      onEditTemplate(template);
    } else {
      navigate('/settings/templates/edit', { state: { template, mode: 'edit' } });
    }
  };

  const handleDuplicateTemplate = useCallback((template: Template) => {
    if (onDuplicateTemplate) {
      onDuplicateTemplate(template);
    } else {
      navigate('/settings/templates/create', {
        state: {
          template: { ...template, name: `${template.name} (COPY)` },
          mode: 'create',
          duplicateFromId: template.id,
        },
      });
    }
  }, [navigate, onDuplicateTemplate]);

  const handleSetAsDefault = (template: Template) => {
    setSelectedTemplate(template);
    setIsDefaultDialogOpen(true);
  };

  const confirmSetAsDefault = () => {
    if (!selectedTemplate) return;

    setLocalTemplateOverrides(templates.map(t => ({
      ...t,
      isDefault: t.type === selectedTemplate.type ? t.id === selectedTemplate.id : t.isDefault,
    })));

    setIsDefaultDialogOpen(false);
    setSelectedTemplate(null);
  };

  const handleDeleteTemplate = (template: Template) => {
    setSelectedTemplate(template);
    setIsDeleteDialogOpen(true);
  };

  const confirmDelete = () => {
    if (!selectedTemplate) return;

    if (isDemo) {
      setLocalTemplateOverrides(templates.filter(t => t.id !== selectedTemplate.id));
      setIsDeleteDialogOpen(false);
      setSelectedTemplate(null);
    } else {
      deleteTemplateMutation.mutate(selectedTemplate.id, {
        onSuccess: () => {
          setIsDeleteDialogOpen(false);
          setSelectedTemplate(null);
        },
        onError: (error) => {
          setIsDeleteDialogOpen(false);
          setSelectedTemplate(null);
          const message = error instanceof ApiError
            ? error.message
            : 'An unexpected error occurred while deleting the template.';
          toast.error(message);
        },
      });
    }
  };

  const getTypeIcon = (type: Template['type']) => {
    switch (type) {
      case 'email':
        return <Mail className="h-4 w-4" />;
      case 'statement':
        return <FileText className="h-4 w-4" />;
    }
  };

  const getTypeBadgeColor = (type: Template['type']) => {
    switch (type) {
      case 'email':
        return 'bg-blue-500/10 text-blue-500 hover:bg-blue-500/20';
      case 'statement':
        return 'bg-purple-500/10 text-purple-500 hover:bg-purple-500/20';
    }
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
    });
  };

  return (
    <div className="flex-1 space-y-6 p-8">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <div className="flex items-center gap-2">
            <h1 className="text-3xl font-bold tracking-tight">Document Templates</h1>
          </div>
          <p className="text-muted-foreground mt-1">
            Create and manage email and statement templates
          </p>
        </div>
        <Button onClick={handleCreateTemplate}>
          <Plus className="h-4 w-4 mr-2" />
          Create Template
        </Button>
      </div>

      {/* Tabs and Content */}
      <Tabs value={activeTab} onValueChange={(value) => setActiveTab(value as 'all' | 'email' | 'statement')}>
        <div className="flex items-center justify-between mb-6">
          <TabsList>
            <TabsTrigger value="all">
              All Templates
              <Badge variant="secondary" className="ml-2">{templateCounts.all}</Badge>
            </TabsTrigger>
            <TabsTrigger value="email">
              <Mail className="h-4 w-4 mr-2" />
              Email
              <Badge variant="secondary" className="ml-2">{templateCounts.email}</Badge>
            </TabsTrigger>
            <TabsTrigger value="statement">
              <FileText className="h-4 w-4 mr-2" />
              Statement
              <Badge variant="secondary" className="ml-2">{templateCounts.statement}</Badge>
            </TabsTrigger>
          </TabsList>

          {/* Search and Sort */}
          <div className="flex items-center gap-4">
            <div className="relative w-[300px]">
              <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
              <Input
                placeholder="Search templates..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="pl-9"
              />
            </div>
            <Select value={sortOption} onValueChange={(value) => setSortOption(value as 'name-asc' | 'name-desc' | 'newest' | 'oldest')}>
              <SelectTrigger className="w-[180px]">
                <SelectValue placeholder="Sort by" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="name-asc">Name A-Z</SelectItem>
                <SelectItem value="name-desc">Name Z-A</SelectItem>
                <SelectItem value="newest">Recently Modified</SelectItem>
                <SelectItem value="oldest">Oldest</SelectItem>
              </SelectContent>
            </Select>
          </div>
        </div>

        <TabsContent value={activeTab} className="mt-0">
          <Card>
            <CardHeader>
              <CardTitle>Templates ({filteredAndSortedTemplates.length})</CardTitle>
              <CardDescription>
                Manage your document templates for emails and statements
              </CardDescription>
            </CardHeader>
            <CardContent>
              {isLoading ? (
                <div className="flex items-center justify-center py-16 gap-2">
                  <Loader2 className="h-8 w-8 animate-spin text-primary" />
                  <span className="text-muted-foreground">Loading templates...</span>
                </div>
              ) : filteredAndSortedTemplates.length === 0 ? (
                <div className="flex flex-col items-center justify-center py-16">
                  <FileText className="h-12 w-12 text-muted-foreground mb-4" />
                  <h3 className="text-lg font-semibold mb-2">No templates found</h3>
                  <p className="text-muted-foreground text-center mb-4">
                    {searchTerm ? 'Try adjusting your search criteria' : 'Get started by creating your first template'}
                  </p>
                  {!searchTerm && (
                    <Button onClick={handleCreateTemplate}>
                      <Plus className="h-4 w-4 mr-2" />
                      Create Template
                    </Button>
                  )}
                </div>
              ) : (
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Name</TableHead>
                      <TableHead>Type</TableHead>
                      <TableHead>Subject/Description</TableHead>
                      <TableHead>Categories</TableHead>
                      <TableHead>Status</TableHead>
                      <TableHead>Last Modified</TableHead>
                      <TableHead className="text-right">Actions</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {filteredAndSortedTemplates.map((template) => (
                      <TableRow key={template.id}>
                        <TableCell>
                          <div className="flex items-center gap-2">
                            {getTypeIcon(template.type)}
                            <span className="font-medium">{template.name}</span>
                          </div>
                        </TableCell>
                        <TableCell>
                          <Badge className={getTypeBadgeColor(template.type)}>
                            {template.type.charAt(0).toUpperCase() + template.type.slice(1)}
                          </Badge>
                        </TableCell>
                        <TableCell>
                          <div className="max-w-md truncate text-sm text-muted-foreground">
                            {template.subject || 'No subject'}
                          </div>
                        </TableCell>
                        <TableCell>
                          {template.category && template.category.length > 0 ? (
                            <div className="flex items-center gap-1 flex-wrap">
                              {template.category.slice(0, 2).map((cat) => (
                                <Badge key={cat} variant="outline" className="text-xs">
                                  {cat}
                                </Badge>
                              ))}
                              {template.category.length > 2 && (
                                <Badge variant="outline" className="text-xs">
                                  +{template.category.length - 2}
                                </Badge>
                              )}
                            </div>
                          ) : (
                            <span className="text-sm text-muted-foreground">-</span>
                          )}
                        </TableCell>
                        <TableCell>
                          {template.isDefault ? (
                            <Badge variant="default" className="bg-yellow-500/10 text-yellow-600 hover:bg-yellow-500/20">
                              <Star className="h-3 w-3 mr-1 fill-current" />
                              Default
                            </Badge>
                          ) : (
                            <span className="text-sm text-muted-foreground">-</span>
                          )}
                        </TableCell>
                        <TableCell>
                          <div className="text-sm text-muted-foreground">
                            {formatDate(template.lastModified)}
                          </div>
                        </TableCell>
                        <TableCell className="text-right">
                          <DropdownMenu>
                            <DropdownMenuTrigger asChild>
                              <Button variant="ghost" size="icon" className="h-8 w-8">
                                <MoreVertical className="h-4 w-4" />
                              </Button>
                            </DropdownMenuTrigger>
                            <DropdownMenuContent align="end">
                              <DropdownMenuItem onClick={() => handleEditTemplate(template)}>
                                <Pencil className="h-4 w-4 mr-2" />
                                Edit
                              </DropdownMenuItem>
                              <DropdownMenuItem onClick={() => handleDuplicateTemplate(template)}>
                                <Copy className="h-4 w-4 mr-2" />
                                Duplicate
                              </DropdownMenuItem>
                              {!template.isDefault && (
                                <DropdownMenuItem onClick={() => handleSetAsDefault(template)}>
                                  <Star className="h-4 w-4 mr-2" />
                                  Set as Default
                                </DropdownMenuItem>
                              )}
                              <DropdownMenuSeparator />
                              <DropdownMenuItem
                                onClick={() => handleDeleteTemplate(template)}
                                className="text-destructive"
                              >
                                <Trash2 className="h-4 w-4 mr-2" />
                                Delete
                              </DropdownMenuItem>
                            </DropdownMenuContent>
                          </DropdownMenu>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              )}
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>

      {/* Set as Default Confirmation Dialog */}
      <AlertDialog open={isDefaultDialogOpen} onOpenChange={setIsDefaultDialogOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Set as Default Template?</AlertDialogTitle>
            <AlertDialogDescription>
              Set <strong>"{selectedTemplate?.name}"</strong> as the default {selectedTemplate?.type} template?
              {templates.find(t => t.type === selectedTemplate?.type && t.isDefault) && (
                <span className="block mt-2 text-yellow-600">
                  This will replace the current default template for this type.
                </span>
              )}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={confirmSetAsDefault}>
              Confirm
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Delete Template Dialog */}
      <AlertDialog open={isDeleteDialogOpen} onOpenChange={setIsDeleteDialogOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete Template?</AlertDialogTitle>
            <AlertDialogDescription>
              Are you sure you want to delete <strong>"{selectedTemplate?.name}"</strong>?
              {selectedTemplate?.isDefault && (
                <span className="block mt-2 text-yellow-600">
                  Warning: This is the default template for {selectedTemplate.type}s.
                </span>
              )}
              <span className="block mt-2">
                This action cannot be undone.
              </span>
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={confirmDelete} className="bg-destructive text-destructive-foreground hover:bg-destructive/90">
              Delete
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
